/**
 * L'interrupteur de l'espace tuteur sur le design system — c'est le BACKEND qui le sert.
 *
 * `static-data` le porte sous `tutor_ds` (`packages/backend/config/tutor_ds.php`) : UN interrupteur
 * pour tout l'espace tuteur, live compris. Trois états côté backend — `off` (personne), `team` (un
 * tuteur qui a accès au back-office (`b_access`), un tuteur de `team_tutor_ids`, un admin qui navigue
 * dans le compte d'un tuteur, ou un compte de test), `all` (tous les tuteurs) — qui
 * arrivent ici comme `{ enabled, user_id }`.
 *
 * **Clé absente = ÉTEINT** (premier lancement,
 * `static-data` pas encore lu, ancien backend), seul un `enabled === true` allume, et **seulement
 * pour le compte qui l'a lu** — `static-data` est persisté et survit à un changement de compte sur
 * l'appareil.
 *
 * Une lecture, `selectTutorDsReading` — la règle (`isTutorDsOnFor`), avec l'attente d'une valeur pas
 * encore relue pour ce compte —, pour la coquille (`BottomTabsNavigator`, l'en-tête de la route
 * `menu`) et le pont des routes poussées (`tutorSpaceBridge`).
 */

/**
 * La règle, pure : allumé si le backend a dit `enabled: true` POUR ce compte-là. Un identifiant
 * manquant d'un côté ou de l'autre éteint.
 */
export function isTutorDsOnFor(served: unknown, currentUserId: unknown): boolean {
    return isServedFor(served, currentUserId) && (served as { enabled?: unknown }).enabled === true
}

/**
 * L'interrupteur tel que la coquille le lit : allumé, éteint, ou **pas encore relu pour ce compte**.
 *
 * Juste après une connexion ou une bascule de compte, la valeur gardée a été calculée pour un autre
 * compte (ou rien n'a été lu) : « éteint » montrerait l'ancien espace le temps de la relecture, à un
 * tuteur qui va avoir le nouveau. C'est `pending` : la coquille attend sur un chargement neutre. Le
 * modèle est l'ancienne attente de l'espace élève (partie avec son interrupteur le 30/10), ses
 * gardes comprises, pour qu'on n'attende jamais pour rien :
 *  - seul un tuteur ou un candidat attend (`waitsForTheReading`) : l'élève et le parent ne changent pas ;
 *  - **une lecture en échec clôt l'attente** (`staticData.lastReadingFailed`, gardé dans le store et
 *    pas dans la tranche d'api, qui l'oublie à la requête suivante) : sinon l'ancien espace, monté sur
 *    « éteint », relit `static-data` à son montage (`ForceUpdateGuard`), l'attente revient, et la
 *    boucle envoie des dizaines de requêtes par seconde ;
 *  - une relecture réussie SANS la clé (un backend revenu en arrière) vaut éteint, même pendant une
 *    relecture en vol : le réducteur écrit alors `null` à la place de la valeur gardée ;
 *  - l'attente a une borne : la coquille l'abandonne au bout de 10 s, même si une lecture ne revient
 *    jamais (`BottomTabsNavigator`) ;
 *  - **l'attente abandonnée** (deux relectures sans valeur pour ce compte, `tutorDsGaveUp`) vaut
 *    éteint, retenue dans le store pour tous les lecteurs ; elle ne se lève que sur une lecture servie
 *    pour ce compte (`isServedFor`).
 */
export type TutorDsReading = "on" | "off" | "pending"

/** Le plus long qu'un tuteur attend la relecture : la coquille l'abandonne, le pont rend le legacy. */
export const TUTOR_SPACE_WAIT_MS = 10_000

/**
 * La valeur servie l'a-t-elle été pour ce compte ? Une seule lecture, pour l'interrupteur et pour la
 * levée de l'abandon (`staticData`) : un abandon levé vaut toujours une valeur du compte.
 */
export function isServedFor(served: unknown, accountId: unknown): boolean {
    return (
        served != null &&
        typeof served === "object" &&
        accountId != null &&
        (served as { user_id?: unknown }).user_id != null &&
        String((served as { user_id?: unknown }).user_id) === String(accountId)
    )
}

type TutorDsReadingState = {
    data?: {
        staticData?: { tutor_ds?: unknown; lastReadingFailed?: boolean; tutorDsGaveUpFor?: unknown }
        currentUser?: { object?: { id?: unknown; roles?: unknown } | null }
    }
}

/**
 * Qui attend la relecture : un tuteur ou un candidat (sa candidature est l'accueil de l'espace
 * tuteur), pas un élève (le rôle d'élève ouvre la coquille de l'élève). Le serveur ne sert
 * l'interrupteur allumé qu'aux rôles `tutor` et `tutor-candidate` (`TutorDsFlags`) : tous les autres
 * lisent éteint sans attendre.
 */
export function waitsForTheReading(roles: unknown): boolean {
    return (
        Array.isArray(roles) &&
        (roles.includes("tutor") || roles.includes("tutor-candidate")) &&
        !roles.includes("student")
    )
}

/**
 * Le compte passe sa candidature : allumé, il n'a que la candidature au design system (sa page, ses
 * routes poussées), même s'il a aussi le rôle `tutor` ; le reste de l'espace tuteur lui garde
 * l'ancien écran (`tutorSpaceBridge`, `TutorNavigator`).
 */
export function isTutorCandidate(roles: unknown): boolean {
    return Array.isArray(roles) && roles.includes("tutor-candidate")
}

export function selectTutorDsReading(state: unknown): TutorDsReading {
    const root = state as TutorDsReadingState | undefined
    const data = root?.data
    const served = data?.staticData?.tutor_ds
    const account = data?.currentUser?.object
    const accountId = account?.id
    if (accountId == null || !waitsForTheReading(account?.roles)) return "off"
    if (isServedFor(served, accountId)) return isTutorDsOnFor(served, accountId) ? "on" : "off"
    const gaveUpFor = data?.staticData?.tutorDsGaveUpFor
    if (gaveUpFor != null && String(gaveUpFor) === String(accountId)) return "off"
    if (data?.staticData?.lastReadingFailed) return "off"
    // `null` : une lecture a réussi SANS la clé (le réducteur l'écrit ; l'état initial la laisse absente).
    // Lu avant la requête en vol : l'ancien espace relit `static-data` à son montage, et une attente
    // rouverte par cette relecture le démonterait, puis le remonterait à la réponse — en boucle.
    if (served === null) return "off"
    return "pending"
}
